import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Tabs, Tab, Paper, Button, Grid, Avatar, Skeleton, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BadgeIcon from '@mui/icons-material/Badge';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { StaffService } from '../../api/services/staffService';
import StaffFormModal from '../../components/StaffFormModal';
import AssignShiftModal from '../../components/AssignShiftModal';

// Tab Components
import StaffDirectoryTab from './components/StaffDirectoryTab';
import DutyScheduleTab from './components/DutyScheduleTab';
import LeaveRequestsTab from './components/LeaveRequestsTab';
import AttendanceLogTab from './components/AttendanceLogTab';
import DepartmentsTab from './components/DepartmentsTab';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function StaffManagement() {
    const [tabValue, setTabValue] = useState(0);
    const [staffRefreshKey, setStaffRefreshKey] = useState(0);
    const { user } = useSelector(state => state.auth);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [assignContext, setAssignContext] = useState({ staffId: null, date: null });

    const hasManageStaff = user?.role?.permissions?.includes('CAN_MANAGE_STAFF') || 
                           user?.customPermissions?.includes('CAN_MANAGE_STAFF') || 
                           user?.role?.permissions?.includes('*') || 
                           user?.customPermissions?.includes('*');

    const AVAILABLE_TABS = React.useMemo(() => {
        return hasManageStaff ? [
            { label: "Staff Directory", id: "directory" },
            { label: "Duty Schedule", id: "schedule" },
            { label: "Leave Requests", id: "leaves" },
            { label: "Attendance Log", id: "attendance" },
            { label: "Departments", id: "departments" }
        ] : [
            { label: "Leave Requests", id: "leaves" }
        ];
    }, [hasManageStaff]);

    // API Data State
    const [staffMembers, setStaffMembers] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [scheduleEvents, setScheduleEvents] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingShifts, setIsLoadingShifts] = useState(false);
    const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
    const [isLoadingDepts, setIsLoadingDepts] = useState(false);
    const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
    const [isLoadingLeaves, setIsLoadingLeaves] = useState(false);
    const [error, setError] = useState('');

    // Schedule Date State
    const [scheduleDate, setScheduleDate] = useState(new Date());

    const formatLocalDate = (d) => {
        if (!d) return '';
        if (typeof d === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
            if (d.includes('T')) return d.split('T')[0];
            const parsed = new Date(d);
            if (!isNaN(parsed.getTime())) {
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, '0');
                const day = String(parsed.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            return d;
        }
        if (d instanceof Date && !isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return '';
    };

    const getWeekRange = (date) => {
        const start = new Date(date);
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(start.setDate(diff));
        monday.setHours(0,0,0,0);
        
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23,59,59,999);
        
        return { start: formatLocalDate(monday), end: formatLocalDate(sunday) };
    };

    const handleOpenForm = (staff = null) => {
        setSelectedStaff(staff);
        setIsFormOpen(true);
    };

    const handleOpenAssignModal = (staffId = null, date = null) => {
        setAssignContext({ staffId, date });
        setIsAssignModalOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setSelectedStaff(null);
    };

    const handleSuccess = (message) => {
        setNotification({ open: true, message, severity: 'success' });
        setStaffRefreshKey(prev => prev + 1);
        const currentTabId = AVAILABLE_TABS[tabValue]?.id;
        if (currentTabId === 'directory' || tabValue === 0) fetchStaff();
        if (currentTabId === 'schedule') { fetchSchedule(); fetchShifts(); }
        if (currentTabId === 'leaves') fetchLeaveRequests();
        if (currentTabId === 'attendance') fetchAttendance();
        if (currentTabId === 'departments' || tabValue === 4) fetchDepartments();
    };

    const handleCloseNotification = () => setNotification({ ...notification, open: false });

    const fetchStaff = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await StaffService.getStaff({});
            setStaffMembers(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || 'Failed to fetch staff members. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchDepartments = useCallback(async () => {
        setIsLoadingDepts(true);
        try {
            const data = await StaffService.getDepartments();
            setDepartments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch departments:', err);
        } finally {
            setIsLoadingDepts(false);
        }
    }, []);

    const fetchShifts = useCallback(async () => {
        setIsLoadingShifts(true);
        try {
            const data = await StaffService.getShifts();
            setShifts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch shifts:', err);
        } finally {
            setIsLoadingShifts(false);
        }
    }, []);

    const fetchSchedule = useCallback(async () => {
        setIsLoadingSchedule(true);
        try {
            const range = getWeekRange(scheduleDate);
            const data = await StaffService.getSchedule({ startDate: range.start, endDate: range.end });
            setScheduleEvents(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch schedule:', err);
            setNotification({ open: true, message: err.message || 'Failed to fetch schedule.', severity: 'error' });
        } finally {
            setIsLoadingSchedule(false);
        }
    }, [scheduleDate]);

    const fetchAttendance = useCallback(async () => {
        setIsLoadingAttendance(true);
        try {
            const data = await StaffService.getAttendance();
            setAttendanceRecords(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch attendance:', err);
            setNotification({ open: true, message: err.message || 'Failed to fetch attendance.', severity: 'error' });
        } finally {
            setIsLoadingAttendance(false);
        }
    }, []);

    const fetchLeaveRequests = useCallback(async () => {
        setIsLoadingLeaves(true);
        try {
            const data = await StaffService.getLeaveRequests();
            setLeaveRequests(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch leave requests:', err);
            setNotification({ open: true, message: err.message || 'Failed to fetch leave requests.', severity: 'error' });
        } finally {
            setIsLoadingLeaves(false);
        }
    }, []);

    useEffect(() => {
        fetchShifts();
        fetchLeaveRequests();
        fetchAttendance();
        const activeTabId = AVAILABLE_TABS[tabValue]?.id;
        switch (activeTabId) {
            case "directory": fetchStaff(); fetchDepartments(); break;
            case "schedule": fetchSchedule(); fetchStaff(); break;
            case "leaves": fetchLeaveRequests(); break;
            case "attendance": fetchAttendance(); break;
            case "departments": fetchDepartments(); fetchStaff(); break;
            default: break;
        }
    }, [tabValue, AVAILABLE_TABS, fetchStaff, fetchShifts, fetchSchedule, fetchDepartments, fetchAttendance, fetchLeaveRequests]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const exportToCSV = (dataArray, filename) => {
        if (!dataArray || dataArray.length === 0) {
            setNotification({ open: true, message: 'No data available to export', severity: 'warning' });
            return;
        }
        
        const flattenObject = (obj, prefix = '') => {
            return Object.keys(obj).reduce((acc, k) => {
                const pre = prefix.length ? prefix + '_' : '';
                if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
                    Object.assign(acc, flattenObject(obj[k], pre + k));
                } else if (!Array.isArray(obj[k])) {
                    acc[pre + k] = obj[k];
                }
                return acc;
            }, {});
        };

        const flatData = dataArray.map(item => flattenObject(item));
        const headers = Array.from(new Set(flatData.flatMap(Object.keys)));
        
        const csvContent = [
            headers.join(','),
            ...flatData.map(row => headers.map(fieldName => `"${String(row[fieldName] || '').replace(/"/g, '""')}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Staff Management</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => handleOpenForm(null)}
                    >
                        Add Staff
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            TOTAL STAFF
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {isLoading ? <Skeleton width={40} /> : staffMembers.length}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Registered staff profiles
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            ON DUTY NOW
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#059669', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {isLoading ? <Skeleton width={40} /> : staffMembers.filter(s => Boolean(s.onDuty)).length}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Active shift personnel
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            PENDING LEAVES
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#d97706', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {isLoadingLeaves ? <Skeleton width={40} /> : leaveRequests.filter(l => l.status === 'Pending').length}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Awaiting approval
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            ATTENDANCE ALERTS
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#dc2626', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {isLoadingAttendance ? <Skeleton width={40} /> : attendanceRecords.filter(a => a.status === 'Late' || a.status === 'Absent' || a.alert || a.hasAlert).length}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Irregularities tracked
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    {AVAILABLE_TABS.map((tab, idx) => (
                        <Tab key={tab.id} label={tab.label} />
                    ))}
                </Tabs>
            </Box>

            <Box>
                    {AVAILABLE_TABS.map((tab, idx) => (
                        <TabPanel key={tab.id} value={tabValue} index={idx}>
                            {tab.id === 'directory' && (
                                <StaffDirectoryTab 
                                    refreshKey={staffRefreshKey}
                                    staffMembers={staffMembers}
                                    departments={departments}
                                    isLoading={isLoading}
                                    error={error}
                                    fetchStaff={fetchStaff}
                                    handleSuccess={handleSuccess}
                                    exportToCSV={exportToCSV}
                                    setNotification={setNotification}
                                    handleOpenForm={handleOpenForm}
                                    handleOpenAssignModal={handleOpenAssignModal}
                                />
                            )}
                            {tab.id === 'schedule' && (
                                <DutyScheduleTab 
                                    staffMembers={staffMembers}
                                    shifts={shifts}
                                    scheduleEvents={scheduleEvents}
                                    isLoadingSchedule={isLoadingSchedule}
                                    handleSuccess={handleSuccess}
                                    scheduleDate={scheduleDate}
                                    setScheduleDate={setScheduleDate}
                                    setShifts={setShifts}
                                    setNotification={setNotification}
                                    handleOpenAssignModal={handleOpenAssignModal}
                                />
                            )}
                            {tab.id === 'leaves' && (
                                <LeaveRequestsTab 
                                    leaveRequests={leaveRequests}
                                    setLeaveRequests={setLeaveRequests}
                                    isLoadingLeaves={isLoadingLeaves}
                                    exportToCSV={exportToCSV}
                                    handleSuccess={handleSuccess}
                                    setNotification={setNotification}
                                />
                            )}
                            {tab.id === 'attendance' && (
                                <AttendanceLogTab 
                                    attendanceRecords={attendanceRecords}
                                    isLoadingAttendance={isLoadingAttendance}
                                    exportToCSV={exportToCSV}
                                    handleSuccess={handleSuccess}
                                    staffMembers={staffMembers}
                                />
                            )}
                            {tab.id === 'departments' && (
                                <DepartmentsTab 
                                    departments={departments}
                                    setDepartments={setDepartments}
                                    isLoadingDepts={isLoadingDepts}
                                    exportToCSV={exportToCSV}
                                    handleSuccess={handleSuccess}
                                    setNotification={setNotification}
                                    staffMembers={staffMembers}
                                    leaveRequests={leaveRequests}
                                />
                            )}
                        </TabPanel>
                    ))}
                </Box>

            <StaffFormModal 
                open={isFormOpen} 
                onClose={handleCloseForm} 
                onSuccess={handleSuccess} 
                staffData={selectedStaff}
            />

            <AssignShiftModal
                open={isAssignModalOpen}
                onClose={() => setIsAssignModalOpen(false)}
                onSuccess={handleSuccess}
                staffList={staffMembers}
                shiftList={shifts}
                initialStaffId={assignContext.staffId}
                initialDate={assignContext.date}
            />

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
